import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../../services/apiService';

// ── tiny helpers ──────────────────────────────────────────────────────────────
const fmt  = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtR = (n) => `₹${fmt(n)}`;
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

const verifyMap = {
  approved:   { label: 'Approved',    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending:    { label: 'Pending',     cls: 'bg-yellow-50  text-yellow-700  border-yellow-200'  },
  inProgress: { label: 'In Progress', cls: 'bg-blue-50    text-blue-700    border-blue-200'    },
  rejected:   { label: 'Rejected',    cls: 'bg-red-50     text-red-700     border-red-200'     },
};

// ── reusable primitives ───────────────────────────────────────────────────────
const Badge = ({ label, cls }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${cls}`}>{label}</span>
);

const Card = ({ title, icon, children, action }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
      <div className="flex items-center gap-2">
        <i className={`bi bi-${icon} text-slate-400 text-sm`}></i>
        <span className="text-sm font-semibold text-slate-700">{title}</span>
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Row = ({ label, value, muted }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-xs font-semibold ${muted ? 'text-slate-400' : 'text-slate-800'}`}>{value ?? '—'}</span>
  </div>
);

const MetricRow = ({ label, value, total, sub }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="py-2 border-b border-slate-50 last:border-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-600">{label}</span>
        <div className="flex items-center gap-2">
          {sub && <span className="text-[11px] text-slate-400">{sub}</span>}
          <span className="text-xs font-bold text-slate-800">{fmt(value)}</span>
        </div>
      </div>
      {total > 0 && (
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#009ef7] rounded-full" style={{ width: `${pct}%` }}></div>
        </div>
      )}
    </div>
  );
};

const InlineStats = ({ items }) => (
  <div className="flex flex-wrap gap-x-6 gap-y-1">
    {items.map(({ label, value, highlight }) => (
      <div key={label} className="flex items-baseline gap-1.5">
        <span className={`text-sm font-bold ${highlight ? 'text-[#009ef7]' : 'text-slate-800'}`}>{value}</span>
        <span className="text-[11px] text-slate-400">{label}</span>
      </div>
    ))}
  </div>
);

// ── main component ────────────────────────────────────────────────────────────
const VendorActivity = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vendorId) return;
    (async () => {
      try {
        setLoading(true);
        const res = await apiService.get(`vendorActivity/${vendorId}`);
        if (res.status) setData(res.data);
        else toast.error(res.message || 'Failed to load');
      } catch { toast.error('Failed to load vendor activity'); }
      finally { setLoading(false); }
    })();
  }, [vendorId]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#009ef7] border-t-transparent mx-auto mb-2"></div>
        <p className="text-xs text-slate-400">Loading...</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <i className="bi bi-exclamation-circle text-3xl text-slate-300 block mb-2"></i>
        <p className="text-sm text-slate-500 mb-3">No data found</p>
        <button onClick={() => navigate(-1)} className="px-4 py-1.5 bg-[#009ef7] text-white rounded-lg text-xs">Go Back</button>
      </div>
    </div>
  );

  const { vendor, services, crm, ai, autoBuy, wallet, transactions, travelLeads } = data;
  const vBadge = verifyMap[vendor.verify] || { label: vendor.verify || 'Unknown', cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  const walletBalance = wallet.balance || 0;
  const totalLeads = crm.leads.total || 1; // avoid /0

  return (
    <div className="min-h-screen bg-[#f8f9fb]">

      {/* ── Header ── */}
      <div className="bg-white border-b border-slate-200 px-5 py-3 flex items-center gap-3 sticky top-0 z-20">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
          <i className="bi bi-arrow-left"></i>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold text-slate-900 truncate">
            {vendor.first_name} {vendor.last_name || ''} — Activity Overview
          </h1>
          <p className="text-[11px] text-slate-400">Vendor ID: {vendorId}</p>
        </div>
        <Link to={`/vendorWallet/${vendorId}`} className="px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors">
          <i className="bi bi-wallet2 mr-1"></i>Wallet
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-5 py-5 space-y-5">

        {/* ── Vendor Profile ── */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex flex-wrap items-start gap-4">
            {/* Avatar */}
            <div className="w-14 h-14 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {vendor.image
                ? <img src={`${process.env.REACT_APP_MEDIA_URL}${vendor.image}`} alt="" className="w-full h-full object-cover" />
                : <i className="bi bi-person-fill text-2xl text-slate-300"></i>
              }
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h2 className="text-base font-bold text-slate-900">{vendor.first_name} {vendor.last_name || ''}</h2>
                <Badge label={vBadge.label} cls={vBadge.cls} />
                {vendor.block === 1 && <Badge label="Blocked" cls="bg-red-50 text-red-700 border-red-200" />}
                {vendor.phone && (
                  <a
                    href={`tel:${vendor.phone}`}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-500 text-white rounded text-[11px] font-semibold hover:bg-emerald-600 transition-colors no-underline"
                  >
                    <i className="bi bi-telephone-fill text-[10px]"></i>Call Now
                  </a>
                )}
              </div>
              <p className="text-xs text-slate-500 mb-2">{vendor.business_name || '—'}</p>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                <span><i className="bi bi-envelope mr-1 text-slate-300"></i>{vendor.email || '—'}</span>
                <span><i className="bi bi-telephone mr-1 text-slate-300"></i>{vendor.phone || '—'}</span>
                <span><i className="bi bi-geo-alt mr-1 text-slate-300"></i>
                  {[vendor.cityId?.name, vendor.stateId?.name, vendor.countryId?.name].filter(Boolean).join(', ') || '—'}
                </span>
                <span><i className="bi bi-calendar3 mr-1 text-slate-300"></i>Joined {fmtD(vendor.createdAt)}</span>
              </div>
              {vendor.services?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {vendor.services.map(s => (
                    <span key={s} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[11px] font-medium capitalize">{s}</span>
                  ))}
                </div>
              )}
              {vendor.locationCover?.length > 0 && (
                <div className="mt-2">
                  <span className="text-[11px] text-slate-400 mr-1"><i className="bi bi-map mr-1"></i>Covers:</span>
                  {vendor.locationCover.map(loc => (
                    <span key={loc._id} className="inline-block mr-1 mb-1 px-2 py-0.5 bg-[#009ef7]/8 text-[#009ef7] border border-[#009ef7]/20 rounded text-[11px] font-medium">
                      {loc.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Quick numbers */}
            <div className="flex flex-wrap gap-4 text-right">
              <div>
                <div className="text-lg font-bold text-slate-900">{fmtR(walletBalance)}</div>
                <div className="text-[11px] text-slate-400">Wallet Balance</div>
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900">{fmt(transactions.completed)}</div>
                <div className="text-[11px] text-slate-400">Completed Txns</div>
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900">{fmt(crm.leads.total)}</div>
                <div className="text-[11px] text-slate-400">CRM Leads</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Row 1: Wallet + Transactions + Travel Leads ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <Card title="Wallet" icon="wallet2">
            <Row label="Total Credit"    value={fmtR(wallet.totalCredit)} />
            <Row label="Total Debit"     value={fmtR(wallet.totalDebit)} />
            <Row label="Expired Balance" value={fmtR(wallet.expiredRemaining)} muted />
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-100">
              <span className="text-xs font-semibold text-slate-600">Net Balance</span>
              <span className={`text-sm font-bold ${walletBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmtR(walletBalance)}</span>
            </div>
          </Card>

          <Card title="Transactions" icon="receipt">
            <Row label="Total Transactions" value={fmt(transactions.total)} />
            <Row label="Completed"          value={fmt(transactions.completed)} />
            <Row label="Pending / Failed"   value={fmt(transactions.total - transactions.completed)} muted />
            <div className="pt-2 mt-1 border-t border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-slate-400">Success rate</span>
                <span className="text-[11px] font-semibold text-slate-700">
                  {transactions.total > 0 ? Math.round((transactions.completed / transactions.total) * 100) : 0}%
                </span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full"
                  style={{ width: `${transactions.total > 0 ? Math.round((transactions.completed / transactions.total) * 100) : 0}%` }}>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Platform Activity" icon="signpost-2">
            <Row label="Travel Leads Purchased" value={fmt(travelLeads.purchased)} />
            <Row label="AutoBuy Active Rules"   value={fmt(autoBuy.activeRules)} />
            <Row label="AI Generations (Total)" value={fmt(ai.totalUsage)} />
            <Row label="AI Success"             value={fmt(ai.successfulGenerations)} />
          </Card>
        </div>

        {/* ── Row 1b: CRM Leads + Contacts ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <Card title="CRM Leads" icon="people"
            action={<InlineStats items={[
              { label: 'total',      value: fmt(crm.leads.total) },
              { label: 'this month', value: fmt(crm.leads.thisMonth), highlight: true },
            ]} />}
          >
            {/* by status */}
            {Object.keys(crm.leads.byStatus).length > 0 && (
              <>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">By Status</p>
                {Object.entries(crm.leads.byStatus)
                  .sort(([,a],[,b]) => b - a)
                  .map(([status, count]) => (
                    <MetricRow key={status} label={status} value={count} total={crm.leads.total} />
                  ))}
              </>
            )}
            {/* by source */}
            {Object.keys(crm.leads.bySource).length > 0 && (
              <>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mt-3 mb-2">By Source</p>
                {Object.entries(crm.leads.bySource)
                  .sort(([,a],[,b]) => b - a)
                  .map(([src, count]) => (
                    <MetricRow key={src} label={src} value={count} total={crm.leads.total} />
                  ))}
              </>
            )}
          </Card>

          <Card title="Contacts" icon="person-lines-fill"
            action={<span className="text-xs font-bold text-slate-700">{fmt(crm.contacts.total)} total</span>}
          >
            {Object.keys(crm.contacts.byType).length > 0 ? (
              <>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">By Type</p>
                {Object.entries(crm.contacts.byType)
                  .sort(([,a],[,b]) => b - a)
                  .map(([type, count]) => (
                    <MetricRow key={type} label={type} value={count} total={crm.contacts.total} />
                  ))}
              </>
            ) : (
              <p className="text-xs text-slate-400 text-center py-4">No contacts yet</p>
            )}
          </Card>
        </div>

        {/* ── Row 1c: Purchased Lead Locations ── */}
        {travelLeads.purchased > 0 && (
          <div className="grid grid-cols-1 gap-4">

            {/* Destinations with count bar + spent + avg budget */}
            <div>
              <Card title="Purchased Lead Destinations" icon="geo-alt"
                action={<span className="text-xs text-slate-400">{travelLeads.topDestinations?.length || 0} destinations</span>}
              >
                {travelLeads.topDestinations?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left py-2 text-[11px] font-semibold text-slate-400 uppercase">Destination</th>
                          <th className="text-right py-2 text-[11px] font-semibold text-slate-400 uppercase">Leads</th>
                          <th className="text-right py-2 text-[11px] font-semibold text-slate-400 uppercase">Spent</th>
                          <th className="text-right py-2 text-[11px] font-semibold text-slate-400 uppercase">Avg Budget</th>
                        </tr>
                      </thead>
                      <tbody>
                        {travelLeads.topDestinations.map((d, i) => {
                          const maxCount = travelLeads.topDestinations[0]?.count || 1;
                          const pct = Math.round((d.count / maxCount) * 100);
                          // find matching route for spent + avgBudget
                          const routeMatch = travelLeads.routes?.find(r => r.to === d.destination);
                          return (
                            <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                              <td className="py-2 pr-3 text-slate-700 font-medium">{d.destination || '—'}</td>
                              <td className="py-2 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                                    <div className="h-full bg-[#009ef7] rounded-full" style={{ width: `${pct}%` }}></div>
                                  </div>
                                  <span className="font-bold text-slate-800">{d.count}</span>
                                </div>
                              </td>
                              <td className="py-2 text-right text-slate-600">{routeMatch ? fmtR(routeMatch.totalSpent) : '—'}</td>
                              <td className="py-2 text-right text-slate-400">{routeMatch?.avgBudget ? fmtR(routeMatch.avgBudget) : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 text-center py-4">No destination data</p>
                )}
              </Card>
            </div>

            {/* Top Origins - commented out for now */}
            {/* <div className="flex flex-col gap-4">
              <Card title="Top Origins" icon="cursor">
                {travelLeads.topOrigins?.length > 0 ? (
                  travelLeads.topOrigins.map((o, i) => (
                    <MetricRow
                      key={i}
                      label={o.origin}
                      value={o.count}
                      total={travelLeads.topOrigins[0]?.count || 1}
                    />
                  ))
                ) : (
                  <p className="text-xs text-slate-400 text-center py-3">No data</p>
                )}
              </Card>
            </div> */}

          </div>
        )}

        {/* ── Row 2: Services ── */}
        <Card title="Services" icon="grid-3x3-gap">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            {[
              { key: 'packages', icon: 'suitcase',  label: 'Packages', to: '/view-packages' },
              { key: 'hotels',   icon: 'building',  label: 'Hotels',   to: '/hotel'         },
              { key: 'vehicles', icon: 'car-front', label: 'Vehicles', to: '/view-vehicles' },
            ].map(({ key, icon, label, to }) => {
              const s = services[key];
              const activePct = s.total > 0 ? Math.round((s.active / s.total) * 100) : 0;
              return (
                <div key={key} className="px-4 py-3 first:pl-0 last:pr-0">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <i className={`bi bi-${icon} text-slate-400 text-sm`}></i>
                      <span className="text-xs font-semibold text-slate-700">{label}</span>
                    </div>
                    <Link
                      to={to}
                      className="text-[11px] text-[#009ef7] hover:underline no-underline flex items-center gap-0.5"
                    >
                      View all <i className="bi bi-arrow-right text-[10px]"></i>
                    </Link>
                  </div>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-2xl font-bold text-slate-900">{s.total}</span>
                    <span className="text-xs text-slate-400">total</span>
                  </div>
                  <div className="flex gap-4 text-xs mb-2">
                    <span className="text-emerald-600 font-medium">{s.active} active</span>
                    <span className="text-slate-400">{s.inactive} inactive</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${activePct}%` }}></div>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">{activePct}% active</div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* ── Row 3: Tasks + Calls ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <Card title="Tasks" icon="check2-square"
            action={<span className="text-xs font-bold text-slate-700">{fmt(crm.tasks.total)} total</span>}
          >
            {Object.keys(crm.tasks.byStatus).length > 0 ? (
              Object.entries(crm.tasks.byStatus)
                .sort(([,a],[,b]) => b - a)
                .map(([status, count]) => (
                  <MetricRow key={status} label={status} value={count} total={crm.tasks.total} />
                ))
            ) : (
              <p className="text-xs text-slate-400 text-center py-4">No tasks yet</p>
            )}
          </Card>

          <Card title="Calls" icon="telephone"
            action={<span className="text-xs font-bold text-slate-700">{fmt(crm.calls.total)} total</span>}
          >
            {Object.keys(crm.calls.byType).length > 0 ? (
              Object.entries(crm.calls.byType)
                .sort(([,a],[,b]) => b - a)
                .map(([type, count]) => (
                  <MetricRow key={type} label={type} value={count} total={crm.calls.total} />
                ))
            ) : (
              <p className="text-xs text-slate-400 text-center py-4">No calls yet</p>
            )}
          </Card>
        </div>

        {/* ── Row 5: Quotations + Payments + Expenses ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <Card title="Quotation Builder" icon="file-earmark-text">
            <Row label="Quotations Sent"  value={fmt(crm.quotationBuilder.quotations)} />
            <Row label="Templates Saved"  value={fmt(crm.quotationBuilder.templates)} />
            <Row label="Lead Quotations"  value={fmt(crm.leadQuotations)} />
          </Card>

          <Card title="Lead Payments" icon="cash-stack">
            <Row label="Total Payments"  value={fmt(crm.payments.total)} />
            <Row label="Total Amount"    value={fmtR(crm.payments.totalAmount)} />
            {crm.payments.total > 0 && (
              <Row label="Avg per Payment"
                value={fmtR(Math.round(crm.payments.totalAmount / crm.payments.total))} muted />
            )}
          </Card>

          <Card title="Expenses" icon="wallet">
            <Row label="Total Expenses"  value={fmt(crm.expenses.total)} />
            <Row label="Total Amount"    value={fmtR(crm.expenses.totalAmount)} />
            {crm.expenses.total > 0 && (
              <Row label="Avg per Expense"
                value={fmtR(Math.round(crm.expenses.totalAmount / crm.expenses.total))} muted />
            )}
          </Card>
        </div>

        {/* ── Row 6: Team ── */}
        <Card title="Team & Roles" icon="people-fill">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100">
            {[
              { label: 'Total Members', value: crm.team.total },
              { label: 'Active',        value: crm.team.active },
              { label: 'Inactive',      value: crm.team.inactive },
              { label: 'Roles Created', value: crm.team.roles },
            ].map(({ label, value }) => (
              <div key={label} className="px-4 py-2 text-center first:pl-0 last:pr-0">
                <div className="text-xl font-bold text-slate-900">{fmt(value)}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
          {crm.team.total > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-slate-400">Active team rate</span>
                <span className="text-[11px] font-semibold text-slate-700">
                  {Math.round((crm.team.active / crm.team.total) * 100)}%
                </span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#009ef7] rounded-full"
                  style={{ width: `${Math.round((crm.team.active / crm.team.total) * 100)}%` }}>
                </div>
              </div>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
};

export default VendorActivity;
